import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAnnualDashboard, formatCurrency, MONTHS, TYPE_COLORS } from '../lib/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, PiggyBank, CreditCard, RefreshCw, ArrowRight } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart
} from 'recharts';

const AnnualReportPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    loadData();
  }, [year]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getAnnualDashboard(year);
      setData(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div className="space-y-6 animate-in" data-testid="annual-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Report Annuale</h1>
          <p className="text-muted-foreground">Analisi delle finanze dell'anno</p>
        </div>
        
        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setYear(y => y - 1)} data-testid="prev-year-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => setYear(y => y + 1)} data-testid="next-year-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={loadData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
          </div>
          <div className="h-80 bg-muted rounded-xl animate-pulse" />
        </div>
      ) : data && (
        <>
          {/* Annual Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-hover" data-testid="annual-income-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Reddito Totale</p>
                    <p className="text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(data.annual_totals.REDDITO)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Media: {formatCurrency(data.annual_totals.REDDITO / 12)}/mese
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="annual-expenses-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Spese Totali</p>
                    <p className="text-2xl font-bold tabular-nums text-red-500">
                      {formatCurrency(data.annual_totals.SPESE)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Media: {formatCurrency(data.annual_totals.SPESE / 12)}/mese
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="annual-savings-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Risparmi Totali</p>
                    <p className="text-2xl font-bold tabular-nums text-blue-500">
                      {formatCurrency(data.annual_totals.RISPARMI)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Media: {formatCurrency(data.annual_totals.RISPARMI / 12)}/mese
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                    <PiggyBank className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover" data-testid="annual-balance-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Bilancio Annuale</p>
                    <p className={`text-2xl font-bold tabular-nums ${data.annual_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                      {formatCurrency(data.annual_balance)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {data.annual_balance >= 0 ? 'In positivo' : 'In negativo'}
                    </p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${data.annual_balance >= 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    <CreditCard className={`w-6 h-6 ${data.annual_balance >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend Chart */}
          <Card data-testid="monthly-trend-chart">
            <CardHeader>
              <CardTitle className="text-lg">Andamento Mensile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.monthly_data}>
                    <defs>
                      <linearGradient id="colorReddito" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(150, 60%, 45%)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(150, 60%, 45%)" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorSpese" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(0, 84%, 60%)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(0, 84%, 60%)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="month_name" />
                    <YAxis tickFormatter={(v) => `€${v}`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="REDDITO" 
                      stroke="hsl(150, 60%, 45%)" 
                      fillOpacity={1} 
                      fill="url(#colorReddito)" 
                      name="Reddito"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="SPESE" 
                      stroke="hsl(0, 84%, 60%)" 
                      fillOpacity={1} 
                      fill="url(#colorSpese)" 
                      name="Spese"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Comparison */}
          <Card data-testid="monthly-comparison-chart">
            <CardHeader>
              <CardTitle className="text-lg">Confronto Mensile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly_data}>
                    <XAxis dataKey="month_name" />
                    <YAxis tickFormatter={(v) => `€${v}`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="REDDITO" fill="hsl(150, 60%, 45%)" name="Reddito" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="SPESE" fill="hsl(0, 84%, 60%)" name="Spese" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="RISPARMI" fill="hsl(190, 70%, 40%)" name="Risparmi" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Balance Trend */}
          <Card data-testid="balance-trend-chart">
            <CardHeader>
              <CardTitle className="text-lg">Saldo Mensile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.monthly_data}>
                    <XAxis dataKey="month_name" />
                    <YAxis tickFormatter={(v) => `€${v}`} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        background: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="balance" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))' }}
                      name="Saldo"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Data Table */}
          <Card data-testid="monthly-data-table">
            <CardHeader>
              <CardTitle className="text-lg">Dettaglio Mensile</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 font-medium">Mese</th>
                      <th className="text-right py-3 px-2 font-medium">Reddito</th>
                      <th className="text-right py-3 px-2 font-medium">Spese</th>
                      <th className="text-right py-3 px-2 font-medium">Risparmi</th>
                      <th className="text-right py-3 px-2 font-medium">Debiti</th>
                      <th className="text-right py-3 px-2 font-medium">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthly_data.map((m, i) => {
                      const hasMovement = (m.REDDITO || 0) + (m.SPESE || 0) + (m.RISPARMI || 0) + (m.DEBITI || 0) > 0;
                      return (
                        <tr
                          key={i}
                          onClick={() => hasMovement && navigate(`/monthly/${year}/${m.month}`)}
                          className={`border-b last:border-0 transition-colors ${
                            hasMovement ? 'cursor-pointer hover:bg-primary/5' : 'opacity-50'
                          }`}
                          data-testid={`row-month-${m.month}`}
                          title={hasMovement ? 'Clicca per dettaglio mensile' : 'Nessun dato'}
                        >
                          <td className="py-3 px-2 font-medium">
                            <span className="inline-flex items-center gap-1">
                              {MONTHS[m.month - 1]}
                              {hasMovement && <ArrowRight className="w-3 h-3 text-muted-foreground/40" />}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right tabular-nums text-emerald-600">{formatCurrency(m.REDDITO)}</td>
                          <td className="py-3 px-2 text-right tabular-nums text-red-500">{formatCurrency(m.SPESE)}</td>
                          <td className="py-3 px-2 text-right tabular-nums text-blue-500">{formatCurrency(m.RISPARMI)}</td>
                          <td className="py-3 px-2 text-right tabular-nums text-orange-500">{formatCurrency(m.DEBITI)}</td>
                          <td className={`py-3 px-2 text-right tabular-nums font-medium ${m.balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {formatCurrency(m.balance)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-semibold">
                      <td className="py-3 px-2">TOTALE</td>
                      <td className="py-3 px-2 text-right tabular-nums text-emerald-600">{formatCurrency(data.annual_totals.REDDITO)}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-red-500">{formatCurrency(data.annual_totals.SPESE)}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-blue-500">{formatCurrency(data.annual_totals.RISPARMI)}</td>
                      <td className="py-3 px-2 text-right tabular-nums text-orange-500">{formatCurrency(data.annual_totals.DEBITI)}</td>
                      <td className={`py-3 px-2 text-right tabular-nums ${data.annual_balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatCurrency(data.annual_balance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AnnualReportPage;
